// src/components/GiftCardPayment.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Gift, Lock, CreditCard } from "lucide-react";
import { api } from "../lib/api";

let stripePromise = null;

const PaymentForm = ({ giftCard, customization, onSuccess, onError }) => {
  const rawFinalAmount = giftCard.finalAmount ?? giftCard.amount;
const finalAmount = Number(rawFinalAmount);
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + "/gift-cards/success",
        },
        redirect: "if_required",
      });

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        const data = await api.giftCards.create({
          amount: giftCard.amount,
          serviceType: giftCard.serviceType,
          senderName: customization.senderName,
          senderEmail: customization.senderEmail,
          recipientName: customization.recipientName,
          recipientEmail: customization.recipientEmail,
          personalMessage: customization.personalMessage,
          deliveryDate: customization.deliveryDate,
          paymentIntentId: paymentIntent.id,
          promoCode: giftCard.promoCode || null,
          originalAmount: giftCard.originalAmount || null,
          discount: giftCard.discount || 0,
          finalAmount,
        });

        onSuccess(data);
      }
    } catch (err) {
      onError(err.message || "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900 text-sm mb-1">
              Secure Payment Processing
            </h3>
            <p className="text-sm text-emerald-800">
              Your payment information is encrypted and secure. You'll be charged{" "}
              <strong>${giftCard.amount}</strong> for this gift card.
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <PaymentElement />
        </CardContent>
      </Card>

      <Button
        type="submit"
        size="lg"
        disabled={!stripe || processing}
        className="w-full"
      >
        {processing ? (
          <>
            <span className="animate-spin mr-2">⏳</span>
            Processing Payment...
          </>
        ) : (
          `Pay $${finalAmount.toFixed(2)}`
        )}
      </Button>


      <p className="text-xs text-center text-gray-500">
        By completing this purchase, you agree to our terms and conditions
      </p>
    </form>
  );
};

export default function GiftCardPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const giftCard = location.state?.giftCard;
  const customization = location.state?.customization;

  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishableKey, setPublishableKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!giftCard || !customization) {
      navigate("/gift-cards");
      return;
    }

    const initializePayment = async () => {
      try {
        const keyData = await api.payment.getPublishableKey();
        setPublishableKey(keyData.publishableKey);

        if (!stripePromise && keyData.publishableKey) {
          stripePromise = loadStripe(keyData.publishableKey);
        }

        const data = await api.giftCards.createPaymentIntent({
        amount: Number(giftCard.finalAmount ?? giftCard.amount),
        senderEmail: customization.senderEmail,
      });


        setClientSecret(data.clientSecret);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializePayment();
  }, [giftCard, customization, navigate]);

  const handleSuccess = (data) => {
    navigate("/gift-cards/success", {
      state: {
        giftCard,
        customization,
        giftCardCode: data.giftCardCode,
        orderId: data.orderId,
      },
    });
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  if (!giftCard || !customization) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto mb-4 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
          <p className="text-gray-600">Initializing secure payment...</p>
        </div>
      </div>
    );
  }

  if (error && !clientSecret) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-red-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-red-500 text-5xl mb-4">⚠️</div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Error</h2>
                <p className="text-gray-600 mb-4">{error}</p>
                <Button onClick={() => navigate("/gift-cards")}>
                  Back to Gift Cards
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#059669",
        colorBackground: "#ffffff",
        colorText: "#1f2937",
        colorDanger: "#dc2626",
        fontFamily: "system-ui, sans-serif",
        borderRadius: "12px",
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate("/gift-cards/customize", { state: { giftCard } })}
            className="text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            ← Back to Customize
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Complete Your Purchase</h1>
              <p className="text-gray-600 mt-1">Secure payment processing</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
                {error}
              </div>
            )}

            <Elements stripe={stripePromise} options={options}>
              <PaymentForm
                giftCard={giftCard}
                customization={customization}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </Elements>
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700 mb-2">Gift Card Value</p>
                  <p className="text-3xl font-bold text-emerald-900">${parseFloat(giftCard.amount).toFixed(2)}</p>
                  {giftCard.serviceType && (
                    <p className="text-sm text-emerald-700 mt-1">{giftCard.serviceType}</p>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">From:</span>
                    <span className="font-medium">{customization.senderName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">To:</span>
                    <span className="font-medium">{customization.recipientName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery:</span>
                    <span className="font-medium">
                      {new Date(customization.deliveryDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {giftCard.promoCode && giftCard.discount > 0 && (
                  <div className="border-t pt-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">${parseFloat(giftCard.originalAmount || giftCard.amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({giftCard.promoCode})</span>
                      <span className="font-medium">-${parseFloat(giftCard.discount).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Total</span>
                    <span className="text-emerald-600">${parseFloat(giftCard.finalAmount || giftCard.amount).toFixed(2)}</span>
                  </div>
                  {giftCard.promoCode && (
                    <p className="text-xs text-green-600 text-center mt-2">
                      🎄 Holiday savings applied!
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
